"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type { LandingSection, CampaignPageSection } from "@growwithai/shared";
import SafeImage from "@/components/common/SafeImage";
import { useTenant, useTenantStoreEnabled, useTenantStoreAsHome } from "@/components/tenant/TenantProvider";
import { EMPTY_LANDING_DATA, parseSections, parseCampaignSections, type LandingData } from "./types";
import ProductOfferSection from "./sections/ProductOfferSection";
import HeroSection from "./sections/HeroSection";
import AboutSection from "./sections/AboutSection";
import CustomHtmlSection from "./sections/CustomHtmlSection";
import FeaturedCoursesSection from "./sections/FeaturedCoursesSection";
import FeaturedExamsSection from "./sections/FeaturedExamsSection";
import PricingSection from "./sections/PricingSection";
import TestimonialsSection from "./sections/TestimonialsSection";
import FaqSection from "./sections/FaqSection";
import CtaWhatsappSection from "./sections/CtaWhatsappSection";
import FooterSection from "./sections/FooterSection";
import GallerySection from "./sections/GallerySection";
import VideoSection from "./sections/VideoSection";
import StatsSection from "./sections/StatsSection";
import TeachersSection from "./sections/TeachersSection";
import LogosSection from "./sections/LogosSection";
import BenefitsSection from "./sections/BenefitsSection";
import CountdownSection from "./sections/CountdownSection";
import PricingTableSection from "./sections/PricingTableSection";
import LinksSection, { type LinksSectionData } from "./sections/LinksSection";
import SocialsSection, { type SocialsSectionData } from "./sections/SocialsSection";
import ParagraphSection from "./sections/ParagraphSection";
import ChecklistSection from "./sections/ChecklistSection";
import ImageSection from "./sections/ImageSection";
import SlideshowSection from "./sections/SlideshowSection";
import ButtonSection from "./sections/ButtonSection";

// Renderer landing tenant — validasi section per item ada di
// parseSections (./types.ts, teruji unit di landing.test.mjs).

/** Navbar mini publik: logo/brand + Masuk + Daftar. */
function LandingNavbar() {
  const $ = _c(18);
  const branding = useTenant();
  const storeEnabled = useTenantStoreEnabled();
  const storeAsHome = useTenantStoreAsHome();
  const showStoreLink = storeEnabled && !storeAsHome;
  const t0 = `${branding.brand_name} beranda`;
  let t1;
  if ($[0] !== branding.brand_name || $[1] !== branding.logo_url) {
    t1 = branding.logo_url ? <SafeImage src={branding.logo_url} alt={branding.brand_name} loading="eager" className="h-8 w-auto" /> : <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--brand-primary)] text-sm font-bold text-[var(--brand-on-primary,#fff)]">{branding.brand_name.charAt(0).toUpperCase()}</span>;
    $[0] = branding.brand_name;
    $[1] = branding.logo_url;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== branding.brand_name) {
    t2 = <span className="truncate font-bold text-warm-black">{branding.brand_name}</span>;
    $[3] = branding.brand_name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = <Link href="/" className="flex min-w-0 items-center gap-2.5" aria-label={t0}>{t1}{t2}</Link>;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== showStoreLink) {
    t4 = showStoreLink && <Link href="/store" className="hidden h-9 items-center justify-center rounded-lg px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-black/[0.04] sm:inline-flex">Toko</Link>;
    $[9] = showStoreLink;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Link href="/login" className="inline-flex h-9 items-center justify-center rounded-lg px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-black/[0.04] sm:px-4">Masuk</Link>;
    t6 = <Link href="/register" className="inline-flex h-9 items-center justify-center rounded-lg bg-[var(--brand-primary)] px-3 sm:px-4 text-sm font-semibold text-[var(--brand-on-primary,#fff)] shadow-sm transition-colors hover:bg-[var(--brand-primary-hover)]">Daftar</Link>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4) {
    t7 = <nav className="flex shrink-0 items-center gap-1.5 sm:gap-2">{t4}{t5}{t6}</nav>;
    $[13] = t4;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t7) {
    t8 = <header className="sticky top-0 z-20 border-b border-black/[0.06] bg-white/80 backdrop-blur"><div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">{t3}{t7}</div></header>;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
export default function LandingRenderer(t0) {
  const $ = _c(31);
  const {
    sections: rawSections,
    data,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "homepage" : t1;
  const isCampaign = variant === "campaign";
  let footerSection;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== data || $[1] !== isCampaign || $[2] !== rawSections || $[3] !== variant) {
    const sections = isCampaign ? parseCampaignSections(rawSections) : parseSections(rawSections);
    const safeData = data ?? EMPTY_LANDING_DATA;
    footerSection = sections.find(_temp) as Extract<LandingSection, {
      type: "footer";
    }> | undefined;
    const bodySections = isCampaign ? sections : sections.filter(_temp2);
    t5 = "min-h-screen bg-white";
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        fontFamily: "var(--brand-font, inherit)"
      };
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    if ($[12] !== isCampaign) {
      t7 = !isCampaign && <LandingNavbar />;
      $[12] = isCampaign;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t2 = "main-content";
    t3 = -1;
    let t8;
    if ($[14] !== isCampaign || $[15] !== safeData || $[16] !== variant) {
      t8 = section => {
        switch (section.type) {
          case "hero":
            {
              return <HeroSection key={section.id} section={section} variant={variant} />;
            }
          case "about":
            {
              return <AboutSection key={section.id} section={section} />;
            }
          case "custom_html":
            {
              return <CustomHtmlSection key={section.id} section={section} />;
            }
          case "featured_courses":
            {
              return <FeaturedCoursesSection key={section.id} section={section} courses={safeData.courses ?? []} />;
            }
          case "featured_exams":
            {
              return <FeaturedExamsSection key={section.id} section={section} exams={safeData.exams ?? []} />;
            }
          case "pricing":
            {
              return <PricingSection key={section.id} section={section} plans={safeData.plans ?? []} />;
            }
          case "testimonials":
            {
              return <TestimonialsSection key={section.id} section={section} />;
            }
          case "faq":
            {
              return <FaqSection key={section.id} section={section} />;
            }
          case "cta_whatsapp":
            {
              return <CtaWhatsappSection key={section.id} section={section} />;
            }
          case "gallery":
            {
              return <GallerySection key={section.id} section={section} />;
            }
          case "video":
            {
              return <VideoSection key={section.id} section={section} />;
            }
          case "stats":
            {
              return <StatsSection key={section.id} section={section} />;
            }
          case "teachers":
            {
              return <TeachersSection key={section.id} section={section} />;
            }
          case "logos":
            {
              return <LogosSection key={section.id} section={section} />;
            }
          case "benefits":
            {
              return <BenefitsSection key={section.id} section={section} />;
            }
          case "countdown":
            {
              return <CountdownSection key={section.id} section={section} />;
            }
          case "pricing_table":
            {
              return <PricingTableSection key={section.id} section={section} />;
            }
          case "links":
            {
              return <LinksSection key={section.id} section={section as LinksSectionData} />;
            }
          case "socials":
            {
              return <SocialsSection key={section.id} section={section as SocialsSectionData} />;
            }
          case "product_offer":
            {
              return <ProductOfferSection key={section.id} section={section} />;
            }
          case "paragraph":
            {
              return <ParagraphSection key={section.id} section={section} />;
            }
          case "checklist":
            {
              return <ChecklistSection key={section.id} section={section} />;
            }
          case "image":
            {
              return <ImageSection key={section.id} section={section} />;
            }
          case "slideshow":
            {
              return <SlideshowSection key={section.id} section={section} />;
            }
          case "button":
            {
              return <ButtonSection key={section.id} section={section} />;
            }
          case "footer":
            {
              return isCampaign ? <FooterSection key={section.id} section={section} /> : null;
            }
          default:
            {
              return null;
            }
        }
      };
      $[14] = isCampaign;
      $[15] = safeData;
      $[16] = variant;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t4 = bodySections.map(t8);
    $[0] = data;
    $[1] = isCampaign;
    $[2] = rawSections;
    $[3] = variant;
    $[4] = footerSection;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    footerSection = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[18] !== t2 || $[19] !== t3 || $[20] !== t4) {
    t8 = <main id={t2} tabIndex={t3}>{t4}</main>;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== footerSection || $[23] !== isCampaign) {
    t9 = !isCampaign && <FooterSection section={footerSection} />;
    $[22] = footerSection;
    $[23] = isCampaign;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = <div className={t5} style={t6}>{t7}{t8}{t9}</div>;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
}
function _temp2(s_0) {
  return s_0.type !== "footer";
}
function _temp(s) {
  return s.type === "footer";
}